/* ============================================================================
   StippleLayer.tsx — renderer pointillisme canvas 2D pour le glyph set
   `cyanotype`. Remplace le rendu ASCII texte (grille typo) par des cercles à
   positions jitterisées + tailles variables, pour reproduire l'effet d'un
   tirage cyanotype (procédé photographique bleu 19e siècle).

   Reçoit la même `grid` string[][] que le `<pre>` ASCII (déjà mappée via le
   glyph set CYANOTYPE = ' ·°∘∙◦○⊙◉●'). Pour chaque cellule, on dérive une
   densité [0..1] depuis l'index du glyph dans le set, puis on tire N dots
   avec positions aléatoires dans la cell + radius proportionnel à la densité.

   Pourquoi pas un canvas plein-écran ? — la grille (cols×rows) reste la base
   d'échantillonnage (cohérent avec le pipeline ASCII existant : density slider,
   dither, brightness/contrast). On casse la trame visible en jitterisant
   massivement les positions intra-cell + en variant les tailles.
   ============================================================================ */

import { useEffect, useRef } from 'react';
import type { CanvasParams } from '../../lib/canvasStore';

/**
 * Le mode cyanotype transgresse délibérément la doctrine grayscale MARTHE :
 * c'est une référence directe au procédé photographique (Anna Atkins, 1843),
 * définit par sa couleur bleu de Prusse caractéristique. Sans bleu, ce n'est
 * plus un cyanotype — c'est juste du pointillisme noir.
 */
const CYANO_BG_CENTER = '#A8BACE'; // bleu lavande clair (halos lumière)
const CYANO_BG_MID = '#7A8EAD'; // bleu de Prusse moyen
const CYANO_BG_EDGE = '#5C6F8C'; // bleu profond aux bords
const CYANO_DOT_COLOR = '#F4F7FB'; // blanc cassé crémeux
const CYANO_DOT_ALPHA = 0.92; // léger transparency pour effet "tâche d'encre"

/** Densité map — doit matcher CYANOTYPE dans lib/ascii/glyphSets.ts */
const CYANO_SET = ' ·°∘∙◦○⊙◉●';

/** Nombre max de dots par cellule (densité 1.0). Au-dessus → effet flou. */
const MAX_DOTS_PER_CELL = 6;

/**
 * Radius base proportionnel au fontSize. Le rendu final aura des dots de
 * radius dans [BASE * 0.3, BASE * 1.0] (modulé par densité + jitter).
 */
const BASE_DOT_RADIUS_RATIO = 0.22;

function glyphToDensity(g: string): number {
  const i = CYANO_SET.indexOf(g);
  if (i < 0) return 0;
  return i / (CYANO_SET.length - 1);
}

/** Mulberry32 — PRNG seedé déterministe (même algo que scatter overlay). */
function makeRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Props {
  grid: string[][];
  params: CanvasParams;
}

export function StippleLayer({ grid, params }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = cvs.getBoundingClientRect();
    if (cvs.width !== rect.width * dpr || cvs.height !== rect.height * dpr) {
      cvs.width = Math.max(1, Math.round(rect.width * dpr));
      cvs.height = Math.max(1, Math.round(rect.height * dpr));
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);

    if (grid.length === 0) return;

    const cols = grid[0]?.length ?? 0;
    const rows = grid.length;
    if (cols === 0) return;

    // ----- Background cyanotype : gradient radial bleu de Prusse + halos -----
    // Reproduit la signature visuelle du cyanotype : fond bleu profond avec
    // des halos plus clairs là où la lumière a "saturé" le tirage.
    const cx = rect.width * 0.4;
    const cy = rect.height * 0.35;
    const maxRadius = Math.hypot(rect.width, rect.height) * 0.75;
    const bgGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, maxRadius);
    bgGrad.addColorStop(0, CYANO_BG_CENTER);
    bgGrad.addColorStop(0.5, CYANO_BG_MID);
    bgGrad.addColorStop(1, CYANO_BG_EDGE);
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Texture marbrée légère : noise basse-fréquence sous forme de taches
    // de gradient additif. Ajoute le grain papier caractéristique des
    // tirages cyanotype d'époque (papier non-uniforme).
    const noiseSeed = (params.seed * 0x85ebca6b) >>> 0;
    const noiseRng = makeRng(noiseSeed);
    ctx.save();
    ctx.globalCompositeOperation = 'overlay';
    for (let i = 0; i < 12; i++) {
      const nx = noiseRng() * rect.width;
      const ny = noiseRng() * rect.height;
      const nr = (0.15 + noiseRng() * 0.25) * Math.min(rect.width, rect.height);
      const g = ctx.createRadialGradient(nx, ny, 0, nx, ny, nr);
      const lighten = noiseRng() > 0.5;
      g.addColorStop(0, lighten ? 'rgba(255,255,255,0.18)' : 'rgba(40,55,80,0.22)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, rect.width, rect.height);
    }
    ctx.restore();

    // ----- Dots blancs cassés par-dessus -----
    const fs = params.fontSize;
    const charW = fs * 0.6 + params.letterSpacing;
    const lh = fs * params.lineHeight;
    const blockW = cols * charW;
    const blockH = rows * lh;
    const ox = (rect.width - blockW) / 2;
    const oy = (rect.height - blockH) / 2;

    const baseR = fs * BASE_DOT_RADIUS_RATIO;
    ctx.fillStyle = CYANO_DOT_COLOR;
    ctx.globalAlpha = CYANO_DOT_ALPHA;

    // PRNG seedé pour reproductibilité (changement de seed = nouveau pattern)
    const seed = (params.seed * 0x9e3779b9) >>> 0;
    const rng = makeRng(seed);

    for (let r = 0; r < rows; r++) {
      const row = grid[r]!;
      const baseY = oy + r * lh;
      for (let c = 0; c < cols; c++) {
        const ch = row[c];
        if (!ch || ch === ' ') continue;
        const density = glyphToDensity(ch);
        if (density <= 0) continue;

        const baseX = ox + c * charW;
        // Nombre de dots : entier en bas, fractionnel via random pour smooth fade
        const nFloat = density * MAX_DOTS_PER_CELL;
        const nFloor = Math.floor(nFloat);
        const nExtra = rng() < nFloat - nFloor ? 1 : 0;
        const n = nFloor + nExtra;

        for (let i = 0; i < n; i++) {
          const jx = rng() * charW;
          const jy = rng() * lh;
          // Radius : modulé par densité (zones plus sombres = points plus gros)
          // + jitter aléatoire (0.7..1.2) pour casser l'uniformité.
          const radius = baseR * (0.35 + 0.65 * density) * (0.7 + rng() * 0.5);
          ctx.beginPath();
          ctx.arc(baseX + jx, baseY + jy, radius, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    // Reset alpha au cas où d'autres canvas overlay réutilisent l'état
    ctx.globalAlpha = 1;
  }, [
    grid,
    params.fontSize,
    params.letterSpacing,
    params.lineHeight,
    params.seed,
  ]);

  return (
    <canvas
      ref={canvasRef}
      className="canvas-stipple"
      data-testid="canvas-stipple"
      aria-hidden="true"
    />
  );
}
